/**
 * Rastreio e previsão de nuvens no mapa: duas subcamadas do Satélite.
 *
 * - **Rastreio**: contorno de cada sistema no horário da imagem exibida; os
 *   núcleos mais frios dentro dele são opção (geral, ou da ficha do sistema).
 * - **Previsão**: o contorno previsto a partir do horário exibido, na
 *   antecedência escolhida ou na do passo do fluxo (`fluxo.ts`) (tracejado e em outra cor: é estimativa, não
 *   observação).
 * - **Trajetória** do sistema selecionado (`trajetoria.ts`): por onde ele
 *   passou e, com o loop parado, por onde a previsão o leva — mesmo com a
 *   Previsão desligada: aí a subcamada da previsão baixa só o arquivo do
 *   quadro na tela, para a ficha e a trajetória, e não desenha contorno nenhum
 *   (`modoDaPrevisao`).
 *
 * A imagem do satélite manda. Os contornos só entram quando são do mesmo
 * horário dela (`tempo.ts`); enquanto o arquivo baixa, ou se ele não existe, o
 * mapa fica só com a imagem — nunca com o contorno de outro horário. A
 * reprodução espera o contorno do próximo quadro por no máximo
 * `PACIENCIA_MS`; depois disso a imagem segue sem ele.
 *
 * Quase sem preenchimento, para não tingir a imagem: o suficiente para o
 * clique dentro do sistema abrir a ficha.
 */
import type { ReactNode } from 'react';
import type Feature from 'ol/Feature';
import type { FeatureLike } from 'ol/Feature';
import { extend, getCenter } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import Polygon from 'ol/geom/Polygon';
import VectorLayer from 'ol/layer/Vector';
import type OlMap from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import { geometriaDoDesenho, suavizarFeicao } from '../map/suavizar';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { isAbortError } from '../lib/fetchRetry';
import { subscribeSelector } from '../lib/store';
import { registerFrameGate } from '../loop/preloader';
import { registerFrameReadiness } from '../loop/render';
import { loopLoadAllowed, useLoopStore } from '../loop/store';
import { GOES_PROJ, registerGoesProjection } from '../map/projection';
import { Z } from '../map/zIndex';
import { useGoesStore } from '../state/goes';
import { shownTimeOf, useTimeStore } from '../state/time';
import { carregarQuadro, RASTREIO_ATRIBUICAO, type QuadroRastreio, type TipoQuadro } from './api';
import type { AreaAEvitar } from '../map/popupControl';
import {
  ancorasDoQuadro,
  chaveDoSistema,
  esquecerPrevisao,
  esquecerQuadro,
  limparRegistro,
  registrarPrevisao,
  registrarQuadro,
} from './registro';
import { ehOSelecionado } from './selecao';
import {
  comAlfa as corComAlfa,
  TRACEJADO,
  TRACO_NUCLEO,
  TRACO_NUCLEO_PREVISTO,
  TRACO_NUCLEO_PREVISTO_SELECIONADO,
  TRACO_NUCLEO_SELECIONADO,
  TRACO_PREVISTO,
  TRACO_PREVISTO_SELECIONADO,
  TRACO_SISTEMA,
  TRACO_SISTEMA_SELECIONADO,
  type Traco,
} from './tracos';
import { antecedenciaNaTela, useTrackingStore, type EstadoNoQuadro, type SistemaNaLista } from './store';
import { antecedenciasDoArquivo, quadroParaInstante } from './tempo';
import { CamadaTrajetoria } from './trajetoria';

/**
 * A história do sistema até o quadro (formato v3): vetores do mesmo tamanho,
 * em ordem crescente de tempo; o último ponto é o próprio quadro (`dt` = 0).
 */
export interface Historico {
  /** Minutos em relação ao quadro (≤ 0). */
  dt: number[];
  km2: (number | null)[];
  tmin: (number | null)[];
  tmed: (number | null)[];
  /** Expansão normalizada da área (10⁻⁶ s⁻¹), como o pyForTraCC a calcula. */
  exp: (number | null)[];
}

/** O que a ficha recebe ao clicar num sistema. */
export interface Sistema {
  previsto: boolean;
  uid: number | null;
  /** Núcleo (limiar mais frio) dentro do sistema `uid`, ou null. */
  iuid: number | null;
  /** Limiar de temperatura de brilho (K). */
  lim: number | null;
  /** Situação: NEW, CON, SPL, MRG… */
  st: string | null;
  /** Tempo de vida (min). */
  vida: number | null;
  km2: number | null;
  tmin: number | null;
  tmed: number | null;
  /** Temperatura de brilho mais quente (K) e desvio-padrão (K). */
  tmax: number | null;
  dp: number | null;
  /** Expansão normalizada da área (10⁻⁶ s⁻¹): positiva, o sistema cresce. */
  exp: number | null;
  /** Núcleos (limiar mais frio) dentro do sistema. */
  nuc: number | null;
  /** História do sistema, ou null (previsto, ou quadro sem ela). */
  hist: Historico | null;
  /** Velocidade (km/h) e rumo (graus a partir do norte, para onde vai); no previsto, o vetor usado. */
  vel: number | null;
  rumo: number | null;
  /** Antecedência (min): só nos previstos. */
  h: number | null;
  /** Horário da imagem de origem (epoch ms). */
  t: number;
}

// As cores e as espessuras moram em `tracos.ts`, de onde a legenda do painel também lê.
export { COR_NUCLEO, COR_PREVISAO, COR_SISTEMA, TRACEJADO } from './tracos';
/** Opacidade da linha do sistema observado. */
export const ALFA_TRACO = TRACO_SISTEMA.alfa;

/** `#rrggbb` → `rgba(r, g, b, alfa)`; sem `alfa`, a do traço do sistema. */
export function comAlfa(hex: string, alfa: number = ALFA_TRACO): string {
  return corComAlfa(hex, alfa);
}

/** Invisível, mas pintado: o clique dentro do contorno acha o sistema. */
const TOQUE = new Fill({ color: 'rgba(255, 255, 255, 0.012)' });

/** O traço em estilos do OpenLayers: o casco (contínuo) por baixo, a linha por cima. */
export function estiloDe(t: Traco): Style[] {
  const lineDash = t.tracejado ? TRACEJADO : undefined;
  return [
    new Style({ geometry: geometriaDoDesenho, stroke: new Stroke({ color: t.casco, width: t.larguraCasco }) }),
    new Style({
      geometry: geometriaDoDesenho,
      fill: t.veu === null ? TOQUE : new Fill({ color: t.veu }),
      stroke: new Stroke({ color: corComAlfa(t.cor, t.alfa), width: t.largura, lineDash }),
    }),
  ];
}

/** Os dois jeitos de desenhar um contorno: normal e selecionado (o da ficha aberta). Estilos prontos, um de cada. */
function familia(normal: Traco, selecionado: Traco): { normal: Style[]; selecionado: Style[] } {
  return { normal: estiloDe(normal), selecionado: estiloDe(selecionado) };
}

const ESTILOS = {
  sistema: familia(TRACO_SISTEMA, TRACO_SISTEMA_SELECIONADO),
  nucleo: familia(TRACO_NUCLEO, TRACO_NUCLEO_SELECIONADO),
  previsto: familia(TRACO_PREVISTO, TRACO_PREVISTO_SELECIONADO),
  nucleoPrevisto: familia(TRACO_NUCLEO_PREVISTO, TRACO_NUCLEO_PREVISTO_SELECIONADO),
};

const PARALELOS = 4;
/** O quanto a reprodução espera um contorno: a imagem tem prioridade. */
export const PACIENCIA_MS = 3000;
/**
 * Quadros guardados já projetados. A previsão pesa ~6× o rastreio: um contorno
 * por antecedência, e são 6 por arquivo tanto no passo de 10 min quanto no de
 * 30 min (o que mudou foi a distância entre elas, não a quantidade).
 */
const MAX_QUADROS: Record<TipoQuadro, number> = { quadros: 288, previsao: 60 };

type Entrada =
  | { estado: 'carregando'; ctrl: AbortController; desde: number }
  | { estado: 'ok'; feicoes: Feature[] }
  | { estado: 'falhou' };

const formato = new GeoJSON();
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Valida a história: vetores do mesmo tamanho, `dt` numérico e crescente. Lixo vira null. */
export function lerHistorico(cru: unknown): Historico | null {
  const h = cru as Partial<Record<keyof Historico, unknown>> | null;
  if (!h || typeof h !== 'object' || !Array.isArray(h.dt) || h.dt.length === 0) return null;
  const n = h.dt.length;
  const dt = h.dt as unknown[];
  if (!dt.every((v, i) => typeof v === 'number' && Number.isFinite(v) && (i === 0 || v > (dt[i - 1] as number)))) return null;
  const serie = (v: unknown): (number | null)[] =>
    Array.isArray(v) && v.length === n ? v.map((x) => num(x)) : Array.from({ length: n }, () => null);
  return { dt: dt as number[], km2: serie(h.km2), tmin: serie(h.tmin), tmed: serie(h.tmed), exp: serie(h.exp) };
}

/** GeoJSON (lon/lat) → features na projeção do mapa; cada contorno leva a ficha do sistema. */
export function lerQuadro(cru: unknown, t: number): Feature[] {
  registerGoesProjection();
  const feicoes = formato.readFeatures(cru, { dataProjection: 'EPSG:4326', featureProjection: GOES_PROJ }) as Feature[];
  for (const f of feicoes) {
    const k = f.get('k') as unknown;
    if (k !== 'c' && k !== 'p') continue;
    suavizarFeicao(f);
    const st = f.get('st') as unknown;
    f.set(
      'sistema',
      {
        previsto: k === 'p',
        uid: num(f.get('uid')),
        iuid: num(f.get('iuid')),
        lim: num(f.get('lim')),
        st: typeof st === 'string' && st ? st : null,
        vida: num(f.get('vida')),
        km2: num(f.get('km2')),
        tmin: num(f.get('tmin')),
        tmed: num(f.get('tmed')),
        tmax: num(f.get('tmax')),
        dp: num(f.get('dp')),
        exp: num(f.get('exp')),
        nuc: num(f.get('nuc')),
        hist: lerHistorico(f.get('hist')),
        vel: num(f.get('vel')),
        rumo: num(f.get('rumo')),
        h: num(f.get('h')),
        t,
      } satisfies Sistema,
      true,
    );
  }
  return feicoes;
}

/** Núcleo = limiar diferente do primeiro (ou, sem a lista, o que tem `iuid`). */
export function ehNucleo(f: FeatureLike, limiares: readonly number[]): boolean {
  const lim = f.get('lim') as unknown;
  if (limiares.length && typeof lim === 'number') return lim !== limiares[0];
  return f.get('iuid') != null;
}

/** Os sistemas (sem os núcleos nem as trajetórias) de um quadro, do maior para o menor. */
export function listarSistemas(feicoes: readonly Feature[], limiares: readonly number[]): SistemaNaLista[] {
  const lista: SistemaNaLista[] = [];
  for (const f of feicoes) {
    const sistema = f.get('sistema') as Sistema | undefined;
    const g = f.getGeometry();
    if (!sistema || sistema.previsto || !g || ehNucleo(f, limiares)) continue;
    const dentro = g instanceof Polygon ? g.getInteriorPoint().getCoordinates() : getCenter(g.getExtent());
    lista.push({ sistema, ponto: [dentro[0], dentro[1]], extensao: g.getExtent() as [number, number, number, number] });
  }
  return lista.sort((a, b) => (b.sistema.km2 ?? 0) - (a.sistema.km2 ?? 0));
}

/**
 * O que uma subcamada faz agora:
 * - `tudo`: desenha os contornos e baixa o loop (depois do primeiro play), e a reprodução a espera;
 * - `selecao`: só a previsão, para o sistema selecionado com a Previsão desligada — baixa o
 *   arquivo do quadro na tela e desenha só o contorno previsto DELE, na antecedência escolhida
 *   na ficha (`antecedenciaDaSelecao`); a reprodução nunca o espera;
 * - `nada`: desligada.
 */
export type ModoDaSubcamada = 'tudo' | 'selecao' | 'nada';

/**
 * O modo da subcamada da previsão. Desligado o satélite, some junto. Com a
 * Previsão ligada, tudo. Desligada, mas com um sistema selecionado e o loop
 * PARADO, o arquivo do quadro na tela vem para a ficha e a trajetória dele:
 * tocando, a extrapolação de um quadro não acompanha o loop, e a reprodução
 * não espera por ela.
 */
export function modoDaPrevisao(previsao: boolean, selecao: boolean, tocando: boolean, satelite: boolean): ModoDaSubcamada {
  if (!satelite) return 'nada';
  if (previsao) return 'tudo';
  return selecao && !tocando ? 'selecao' : 'nada';
}

interface Config {
  tipo: TipoQuadro;
  id: string;
  titulo: string;
  zIndex: number;
  modo: () => ModoDaSubcamada;
  /** Há arquivo deste tipo para o quadro? */
  tem: (q: QuadroRastreio) => boolean;
  estilo: (f: FeatureLike) => Style[] | undefined;
  aoMudar: (estado: EstadoNoQuadro) => void;
  /** Um quadro entrou na memória, ou saiu dela. */
  aoCarregar?: (t: number, feicoes: readonly Feature[]) => void;
  aoDescartar?: (t: number) => void;
  /** O que está desenhado mudou. `carregado`: há arquivo na tela (mesmo que sem contorno). */
  aoTrocar?: (feicoes: readonly Feature[], carregado: boolean) => void;
}

/** Uma subcamada: a camada vetorial, os quadros baixados e a fila. */
class Subcamada {
  readonly layer: VectorLayer<VectorSource>;
  private readonly source: VectorSource;
  private readonly cache = new Map<string, Entrada>();
  private fila: QuadroRastreio[] = [];
  private emVoo = 0;
  private naTela: string | null = null;
  private tempos: number[] = [];
  private quadros: QuadroRastreio[] = [];
  private esperas = new Map<string, (() => void)[]>();
  private readonly tempoDe = new Map<string, number>();

  constructor(
    private readonly map: OlMap,
    private readonly cfg: Config,
    renderPopup: (s: Sistema) => ReactNode | null,
  ) {
    this.source = new VectorSource({ attributions: RASTREIO_ATRIBUICAO });
    this.layer = new VectorLayer({
      source: this.source,
      style: cfg.estilo,
      zIndex: cfg.zIndex,
      className: `ol-layer amaview-${cfg.id}`,
      properties: { id: cfg.id, title: cfg.titulo },
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });
    // Fichas arrastáveis: o usuário as tira de cima do sistema para investigá-lo.
    this.layer.set('popupMovel', true);
    // No desktop a ficha não cobre o contorno do sistema dela: abre ao lado (map/popupArea).
    this.layer.set('popupEvitar', (feature: FeatureLike) => {
      const s = feature.get('sistema') as Sistema | undefined;
      return s ? areaDoSistema(s) : null;
    });
    this.layer.set('popup', (feature: FeatureLike): ReactNode | null => {
      const s = feature.get('sistema') as Sistema | undefined;
      return s ? renderPopup(s) : null;
    });
    map.addLayer(this.layer);
  }

  private quadroDe(t: number | null): QuadroRastreio | null {
    const q = quadroParaInstante(this.quadros, this.tempos, t);
    return q && this.cfg.tem(q) ? q : null;
  }

  /** O contorno do instante `t` já pode ser desenhado, não existe, ou já se esperou demais por ele? */
  pronto(t: number | null, agora: number = Date.now()): boolean {
    if (this.cfg.modo() !== 'tudo') return true;
    const q = this.quadroDe(t);
    if (!q) return true;
    const e = this.cache.get(q.c);
    // Fora do plano (loop maior que o cache): chega quando o quadro for exibido.
    if (!e) return true;
    return e.estado !== 'carregando' || agora - e.desde > PACIENCIA_MS;
  }

  /** Para a exportação: espera o arquivo do quadro NA TELA chegar (ou falhar). */
  quandoPronto(t: number | null): Promise<void> | void {
    if (this.cfg.modo() !== 'tudo') return;
    const q = this.quadroDe(t);
    if (!q) return;
    const e = this.cache.get(q.c);
    if (e && e.estado !== 'carregando') return;
    if (!e) this.pedir(q);
    return new Promise((resolve) => {
      const lista = this.esperas.get(q.c) ?? [];
      lista.push(resolve);
      this.esperas.set(q.c, lista);
    });
  }

  /** Quadros necessários para o loop atual; baixa o que falta e descarta o que saiu. */
  replanejar(indice: readonly QuadroRastreio[]): void {
    const modo = this.cfg.modo();
    // Em `selecao` os contornos estão na memória (a ficha e a trajetória os leem), mas não no mapa.
    // No modo seleção a camada fica à vista: o estilo só desenha o previsto do sistema selecionado.
    this.layer.setVisible(modo !== 'nada');
    this.quadros = [...indice];
    this.tempos = this.quadros.map((q) => q.t);
    if (modo === 'nada') {
      this.fila = [];
      for (const e of this.cache.values()) if (e.estado === 'carregando') e.ctrl.abort();
      for (const [c, e] of this.cache) if (e.estado === 'carregando') this.descartar(c);
      this.mostrar();
      return;
    }
    const { frames, index } = useTimeStore.getState();
    const precisa = new Map<string, QuadroRastreio>();
    // Do quadro atual para trás: o que está na tela chega primeiro. Antes do
    // primeiro play só o quadro na tela e os já baixados (loop/preloader.ts).
    const todoLoop = modo === 'tudo' && loopLoadAllowed();
    const max = MAX_QUADROS[this.cfg.tipo];
    for (let k = 0; k < frames.length && precisa.size < max; k++) {
      const i = (index - k + frames.length) % frames.length;
      const q = this.quadroDe(frames[i]);
      if (!q) continue;
      if (todoLoop || i === index || this.cache.get(q.c)?.estado === 'ok') precisa.set(q.c, q);
    }
    for (const [c, e] of this.cache) {
      if (precisa.has(c)) continue;
      if (e.estado === 'carregando') e.ctrl.abort();
      this.descartar(c);
    }
    this.fila = [...precisa.values()].filter((q) => !this.cache.has(q.c));
    this.bombear();
    this.mostrar();
  }

  private descartar(c: string): void {
    this.cache.delete(c);
    const t = this.tempoDe.get(c);
    if (t !== undefined) {
      this.tempoDe.delete(c);
      this.cfg.aoDescartar?.(t);
    }
  }

  private pedir(q: QuadroRastreio): void {
    if (this.cache.has(q.c)) return;
    this.fila = this.fila.filter((x) => x.c !== q.c);
    this.fila.unshift(q);
    this.bombear();
  }

  /** O cache não cresce sem limite quando o loop é maior que ele: sai o mais antigo que não está na tela. */
  private aparar(): void {
    const max = MAX_QUADROS[this.cfg.tipo];
    if (this.cache.size <= max) return;
    for (const [c, e] of this.cache) {
      if (this.cache.size <= max) break;
      if (e.estado !== 'carregando' && c !== this.naTela) this.descartar(c);
    }
  }

  private bombear(): void {
    while (this.emVoo < PARALELOS && this.fila.length) {
      const q = this.fila.shift()!;
      if (this.cache.has(q.c)) continue;
      const ctrl = new AbortController();
      this.cache.set(q.c, { estado: 'carregando', ctrl, desde: Date.now() });
      this.emVoo++;
      carregarQuadro(this.cfg.tipo, q.c, ctrl.signal)
        .then((cru) => {
          if (this.cache.get(q.c)?.estado !== 'carregando') return;
          const feicoes = lerQuadro(cru, q.t);
          this.cache.set(q.c, { estado: 'ok', feicoes });
          this.tempoDe.set(q.c, q.t);
          this.cfg.aoCarregar?.(q.t, feicoes);
          this.aparar();
        })
        .catch((e: unknown) => {
          if (isAbortError(e) || this.cache.get(q.c)?.estado !== 'carregando') return;
          console.warn(`[AmaView] Rastreio (${this.cfg.tipo}) ${q.c} não carregou:`, e);
          this.cache.set(q.c, { estado: 'falhou' });
        })
        .finally(() => {
          this.emVoo--;
          this.mostrar();
          // Depois de desenhar: a exportação espera o quadro NA TELA.
          const esperas = this.esperas.get(q.c);
          if (esperas && this.cache.get(q.c)?.estado !== 'carregando') {
            this.esperas.delete(q.c);
            esperas.forEach((r) => r());
          }
          this.bombear();
        });
    }
  }

  /** As feições na tela. */
  feicoesNaTela(): Feature[] {
    return this.source.getFeatures();
  }

  /** Recoloca na fila os quadros que falharam. Devolve se havia algum. */
  esquecerFalhas(): boolean {
    let algum = false;
    for (const [c, e] of this.cache) {
      if (e.estado === 'falhou') {
        this.descartar(c);
        algum = true;
      }
    }
    return algum;
  }

  /** Desenha o quadro do instante exibido — ou nada, se ele não está pronto. */
  mostrar(): void {
    const q = this.cfg.modo() !== 'nada' ? this.quadroDe(shownTimeOf(useTimeStore.getState())) : null;
    let estado: EstadoNoQuadro;
    if (!q) {
      this.trocar(null, []);
      estado = 'sem';
    } else {
      const e = this.cache.get(q.c);
      if (e?.estado === 'ok') {
        this.trocar(q.c, e.feicoes);
        estado = 'ok';
      } else {
        // Baixando ou falhou: só a imagem, nunca o contorno de outro horário.
        this.trocar(null, []);
        estado = e?.estado === 'falhou' ? 'falhou' : 'carregando';
        if (!e) this.pedir(q);
      }
    }
    this.cfg.aoMudar(estado);
  }

  private trocar(c: string | null, feicoes: Feature[]): void {
    if (c === this.naTela) return;
    this.naTela = c;
    this.source.clear(true);
    if (feicoes.length) this.source.addFeatures(feicoes);
    this.cfg.aoTrocar?.(feicoes, c !== null);
  }

  dispose(): void {
    for (const e of this.cache.values()) if (e.estado === 'carregando') e.ctrl.abort();
    for (const t of this.tempoDe.values()) this.cfg.aoDescartar?.(t);
    this.tempoDe.clear();
    this.cache.clear();
    this.esperas.forEach((l) => l.forEach((r) => r()));
    this.esperas.clear();
    this.source.clear(true);
    this.map.removeLayer(this.layer);
  }
}

/** O núcleo aparece com a opção geral ligada, ou quando a ficha do sistema dele pediu. */
function nucleoNoMapa(f: FeatureLike): boolean {
  const { nucleos, nucleosDe } = useTrackingStore.getState();
  return nucleos || (nucleosDe !== null && f.get('uid') === nucleosDe);
}

/** O gerenciador no mapa, para quem precisa do contorno que está na tela (a ficha do sistema). */
let ativo: TrackingLayerManager | null = null;

/**
 * A área que a ficha de `s` não deve cobrir: o contorno dele como está na
 * tela AGORA (o sistema anda e muda de forma com o player) e, com ele, o cone
 * de incerteza (09/10/2026: a ficha cobria o começo do cone). Sem `uid` não há
 * como achá-lo de novo: a ficha abre sobre o ponto, como as outras.
 */
export function areaDoSistema(s: Pick<Sistema, 'lim' | 'uid' | 'iuid' | 'previsto'>): AreaAEvitar | null {
  if (s.uid === null) return null;
  const chave = chaveDoSistema(s);
  return () => {
    const sistema = ativo?.extensaoDe(chave, s.previsto) ?? null;
    const cone = ativo?.extensaoDoCone() ?? null;
    if (!sistema || !cone) return sistema;
    return extend([...sistema], cone);
  };
}

const sateliteLigado = () => useGoesStore.getState().layers.some((l) => l.visible);

/** O modo da previsão agora (ver `modoDaPrevisao`). */
function modoDaPrevisaoAgora(): ModoDaSubcamada {
  const s = useTrackingStore.getState();
  // A exportação conta como "tocando": ela não espera o arquivo do modo seleção, e o vídeo sairia com a trajetória prevista em uns quadros e não em outros.
  const loop = useLoopStore.getState();
  return modoDaPrevisao(s.previsao, s.selecao !== null, loop.playing || loop.exportLocked, sateliteLigado());
}

export class TrackingLayerManager {
  private readonly rastreio: Subcamada;
  private readonly previsao: Subcamada;
  private readonly trajetoria: CamadaTrajetoria;
  private readonly paradas: (() => void)[] = [];

  constructor(map: OlMap, renderPopup: (s: Sistema) => ReactNode | null) {
    const limiares = () => useTrackingStore.getState().indice?.limiares ?? [];
    /** O jeito de desenhar `f`: o selecionado (o da ficha aberta), ou normal. */
    const papel = (f: FeatureLike): 'selecionado' | 'normal' => {
      const s = f.get('sistema') as Sistema | undefined;
      return s && ehOSelecionado(useTrackingStore.getState().selecao, s) ? 'selecionado' : 'normal';
    };
    ativo = this;
    const publicar = (chave: 'rastreio' | 'previsao') => (estado: EstadoNoQuadro) => {
      const antes = useTrackingStore.getState().noQuadro;
      if (antes[chave] !== estado) useTrackingStore.setState({ noQuadro: { ...antes, [chave]: estado } });
    };
    this.rastreio = new Subcamada(
      map,
      {
        tipo: 'quadros',
        id: 'tracking',
        titulo: 'Rastreio de nuvens',
        zIndex: Z.TRACKING,
        // Subcamadas do Satélite: desligado ele, somem junto.
        modo: () => (useTrackingStore.getState().rastreio && sateliteLigado() ? 'tudo' : 'nada'),
        tem: () => true,
        estilo: (f) => {
          // A trajetória vem no arquivo, mas não é desenhada; o rumo do sistema está na ficha dele.
          if (f.get('k') === 't') return undefined;
          // Os limiares internos são opção: o que importa de cara é o contorno externo.
          if (ehNucleo(f, limiares())) return nucleoNoMapa(f) ? ESTILOS.nucleo[papel(f)] : undefined;
          return ESTILOS.sistema[papel(f)];
        },
        aoMudar: publicar('rastreio'),
        aoTrocar: (feicoes) => useTrackingStore.setState({ sistemas: listarSistemas(feicoes, limiares()) }),
        aoCarregar: (t, feicoes) =>
          registrarQuadro(
            t,
            feicoes.map((f) => f.get('sistema') as Sistema | undefined).filter((x): x is Sistema => !!x),
            ancorasDoQuadro(feicoes),
          ),
        aoDescartar: esquecerQuadro,
      },
      renderPopup,
    );
    this.previsao = new Subcamada(
      map,
      {
        tipo: 'previsao',
        id: 'forecast',
        titulo: 'Previsão de nuvens',
        zIndex: Z.TRACKING_FORECAST,
        modo: modoDaPrevisaoAgora,
        tem: (q) => q.prev,
        estilo: (f) => {
          // Previsão desligada: só o sistema da ficha, na antecedência escolhida nela — os demais
          // não são projetados (pedido do usuário, 08/10/2026).
          // Ligada, mas numa imagem anterior à origem: previsões velhas não interessam — também só o
          // sistema da ficha, se ela pediu.
          const st = useTrackingStore.getState();
          if (!st.previsao || !st.naOrigem) {
            const s = f.get('sistema') as Sistema | undefined;
            if (!s || papel(f) !== 'selecionado' || st.antecedenciaDaSelecao === null || s.h !== st.antecedenciaDaSelecao) return undefined;
            return ehNucleo(f, limiares()) ? ESTILOS.nucleoPrevisto.selecionado : ESTILOS.previsto.selecionado;
          }
          // Antecedência 0 = observação: nenhum polígono previsto.
          if (f.get('h') !== antecedenciaNaTela(useTrackingStore.getState())) return undefined;
          if (ehNucleo(f, limiares())) return nucleoNoMapa(f) ? ESTILOS.nucleoPrevisto[papel(f)] : undefined;
          return ESTILOS.previsto[papel(f)];
        },
        aoMudar: publicar('previsao'),
        aoCarregar: registrarPrevisao,
        aoDescartar: esquecerPrevisao,
        // O arquivo diz que antecedências o quadro tem: os antigos (passo de 10 min) não têm as do índice novo.
        aoTrocar: (feicoes, carregado) =>
          useTrackingStore.setState({
            hNoQuadro: carregado ? antecedenciasDoArquivo(feicoes.map((f) => (f.get('sistema') as Sistema | undefined)?.h)) : null,
          }),
      },
      renderPopup,
    );

    this.trajetoria = new CamadaTrajetoria(map);

    this.paradas.push(
      useTrackingStore.subscribe((a, b) => {
        if (antecedenciaNaTela(a) !== antecedenciaNaTela(b) || a.antecedenciaDaSelecao !== b.antecedenciaDaSelecao || a.naOrigem !== b.naOrigem) {
          this.previsao.layer.changed();
        }
        if (a.selecao !== b.selecao) {
          this.rastreio.layer.changed();
          this.previsao.layer.changed();
        }
        if (a.nucleos !== b.nucleos || a.nucleosDe !== b.nucleosDe) {
          this.rastreio.layer.changed();
          this.previsao.layer.changed();
        }
        if (a.rastreio !== b.rastreio || a.previsao !== b.previsao || a.indice !== b.indice) this.replanejar();
        // Selecionar (ou soltar) um sistema com a Previsão desligada liga (ou desliga) a carga para ele.
        else if ((a.selecao === null) !== (b.selecao === null)) this.replanejar();
      }),
      // Tocando, a previsão do selecionado não é baixada; parado, volta a ser.
      subscribeSelector(useLoopStore, (s) => s.playing, () => this.replanejar()),
      subscribeSelector(useGoesStore, (s) => s.layers.some((l) => l.visible), () => this.replanejar()),
      useTimeStore.subscribe((a, b) => {
        // Antes do primeiro play cada quadro novo na tela pede o seu contorno (sob demanda).
        if (a.frames !== b.frames || (a.index !== b.index && !loopLoadAllowed())) this.replanejar();
        else if (shownTimeOf(a) !== shownTimeOf(b)) this.mostrar();
      }),
      subscribeSelector(useLoopStore, (s) => loopLoadAllowed(s), () => this.replanejar()),
      registerFrameGate((i) => {
        const t = useTimeStore.getState().frames[i] ?? null;
        return this.rastreio.pronto(t) && this.previsao.pronto(t);
      }),
      registerFrameReadiness(() => {
        const t = shownTimeOf(useTimeStore.getState());
        const esperas = [this.rastreio.quandoPronto(t), this.previsao.quandoPronto(t)].filter((p): p is Promise<void> => !!p);
        return esperas.length ? Promise.all(esperas).then(() => undefined) : undefined;
      }),
    );
    this.replanejar();
  }

  /**
   * A extensão (projeção do mapa) do contorno do sistema `chave` que está na
   * tela agora — observado, ou previsto na antecedência desenhada —, ou null
   * se ele não existe neste quadro. É a área que a ficha do sistema não cobre.
   */
  /** A extensão do cone de incerteza do selecionado na tela, ou null sem cone. */
  extensaoDoCone(): number[] | null {
    return this.trajetoria.extensaoDoCone();
  }

  extensaoDe(chave: string, previsto: boolean): number[] | null {
    const h = antecedenciaNaTela(useTrackingStore.getState());
    for (const f of (previsto ? this.previsao : this.rastreio).feicoesNaTela()) {
      const s = f.get('sistema') as Sistema | undefined;
      if (!s || s.uid === null || chaveDoSistema(s) !== chave || (previsto && s.h !== h)) continue;
      const extensao = f.getGeometry()?.getExtent();
      if (extensao) return extensao;
    }
    return null;
  }

  private replanejar(): void {
    const quadros = useTrackingStore.getState().indice?.quadros ?? [];
    this.rastreio.replanejar(quadros);
    this.previsao.replanejar(quadros);
  }

  private mostrar(): void {
    this.rastreio.mostrar();
    this.previsao.mostrar();
  }

  /** Recoloca na fila os quadros que falharam (ex.: a conexão caiu por um minuto). */
  tentarDeNovo(): void {
    const a = this.rastreio.esquecerFalhas();
    const b = this.previsao.esquecerFalhas();
    if (a || b) this.replanejar();
  }

  dispose(): void {
    if (ativo === this) ativo = null;
    this.paradas.forEach((p) => p());
    this.trajetoria.dispose();
    this.rastreio.dispose();
    this.previsao.dispose();
    limparRegistro();
  }
}
